const INLINE_ARRAY_MAX_LENGTH = 80;

const JS_STRING_ESCAPES: [searchValue: string, replaceValue: string][] = [
	['\\', String.raw`\\`],
	['\'', String.raw`\'`],
	['\r', String.raw`\r`],
	['\n', String.raw`\n`],
	['\t', String.raw`\t`],
	['\u2028', String.raw`\u2028`],
	['\u2029', String.raw`\u2029`],
];

export function serializeJavaScriptValue(value: unknown, indentation = '\t', indentLevel = 0): string {
	if (Array.isArray(value)) {
		return serializeJavaScriptArray(value, indentLevel, indentation);
	}

	if (value && typeof value === 'object') {
		return serializeJavaScriptRecord(value, indentLevel, indentation);
	}

	if (typeof value === 'string') {
		return quoteJavaScriptString(value);
	}

	if (typeof value === 'number') {
		return String(value);
	}

	if (typeof value === 'boolean') {
		return value ? 'true' : 'false';
	}

	if (value === undefined) {
		return 'undefined';
	}

	return 'null';
}

function indent(level: number, indentation: string): string {
	return indentation.repeat(level);
}

function serializeJavaScriptArray(value: unknown[], indentLevel: number, indentation: string): string {
	if (value.length === 0) {
		return '[]';
	}

	const serializedItems = value.map((item) => serializeJavaScriptValue(item, indentation, indentLevel + 1));
	const inline = `[${serializedItems.join(', ')}]`;

	// Keep compact primitive arrays readable without forcing every field list onto multiple lines.
	if (serializedItems.every((item) => !item.includes('\n')) && inline.length <= INLINE_ARRAY_MAX_LENGTH) {
		return inline;
	}

	const itemIndent = indent(indentLevel + 1, indentation);
	const closingIndent = indent(indentLevel, indentation);

	return `[\n${serializedItems.map((item) => `${itemIndent}${item},`).join('\n')}\n${closingIndent}]`;
}

function serializeJavaScriptRecord(value: object, indentLevel: number, indentation: string): string {
	const entries = Object.entries(value);

	if (entries.length === 0) {
		return '{}';
	}

	const propertyIndent = indent(indentLevel + 1, indentation);
	const closingIndent = indent(indentLevel, indentation);
	const properties = entries.map(([key, propertyValue]) => `${propertyIndent}${formatJavaScriptRecordKey(key)}: ${serializeJavaScriptValue(propertyValue, indentation, indentLevel + 1)},`);

	return `{\n${properties.join('\n')}\n${closingIndent}}`;
}

function formatJavaScriptRecordKey(key: string): string {
	const isValidIdentifier = /^[a-z_$][\w$]*$/i.test(key);
	return isValidIdentifier ? key : quoteJavaScriptString(key);
}

function quoteJavaScriptString(value: string): string {
	let result = value;

	// Escape backslashes first so existing escape sequences are preserved literally.
	for (const [searchValue, replaceValue] of JS_STRING_ESCAPES) {
		result = result.replaceAll(searchValue, replaceValue);
	}

	return `'${result}'`;
}
